import React, { useState, useEffect } from 'react';
import { PauseCircle, Play } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * PausedBanner — shown when a game is paused.
 * Scorer sees a Resume button. Viewers see read-only status with countdown.
 *
 * Props:
 * - pauseReason: string
 * - autoResumeAt: ISO string or null (indefinite)
 * - isScorer: boolean
 * - onResume: callback (scorer only)
 * - resuming: boolean (loading state)
 */
const PausedBanner = ({ pauseReason, autoResumeAt, isScorer, onResume, resuming = false }) => {
  const { theme } = useTheme();
  const [remaining, setRemaining] = useState('');

  useEffect(() => {
    if (!autoResumeAt) {
      setRemaining('');
      return;
    }

    const update = () => {
      const diff = new Date(autoResumeAt).getTime() - Date.now();
      if (diff <= 0) {
        setRemaining('Resuming...');
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setRemaining(`${mins}:${secs.toString().padStart(2, '0')}`);
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [autoResumeAt]);

  return (
    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-500/20 rounded-full animate-pulse">
            <PauseCircle className="w-6 h-6 text-amber-500" />
          </div>
          <div>
            <p className={`font-semibold ${theme.text}`}>Game Paused</p>
            <p className={`text-sm ${theme.textSecondary}`}>
              {pauseReason || 'No reason provided'}
              {remaining && ` · Resumes in ${remaining}`}
              {!autoResumeAt && ' · Indefinite'}
            </p>
          </div>
        </div>

        {isScorer && (
          <button
            onClick={onResume}
            disabled={resuming}
            className="flex items-center gap-1.5 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium text-sm disabled:opacity-50"
          >
            <Play className="w-4 h-4" />
            {resuming ? 'Resuming...' : 'Resume'}
          </button>
        )}
      </div>
    </div>
  );
};

export default PausedBanner;
